/*
 * The detail overlay's open/close card morph — ported from the handoff's
 * `morph()`.
 *
 * A clone of the clicked card grows from its exact on-screen rect into the sheet
 * (the FLIP technique: measure the card before, the sheet after) while flipping
 * rotateY 0 → 180°, so the card's face turns into the paper back. Close is the
 * reverse. The real sheet is hidden behind the clone until the flight lands.
 *
 * No React here: the clone is a `cloneNode` of the live card, so nothing needs to
 * re-render. Under reduced motion the caller skips this and shows the sheet at
 * once.
 */

const DURATION = 820
const EASE = "cubic-bezier(.7,0,.2,1)"

interface Box {
  left: number
  top: number
  width: number
  height: number
}

/** The sheet can be taller than the viewport; the card only flies to what shows. */
function clipToView(r: DOMRect): Box {
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: Math.min(r.height, innerHeight - r.top - 16),
  }
}

const px = (b: Box) => ({
  left: `${b.left}px`,
  top: `${b.top}px`,
  width: `${b.width}px`,
  height: `${b.height}px`,
})

/** Build the flipping morph element (card clone on the front, paper on the back). */
function makeMorph(
  cardEl: HTMLElement,
  start: Box
): { morph: HTMLElement; inner: HTMLElement; back: HTMLElement } {
  const morph = document.createElement("div")
  morph.style.cssText = `position:fixed;z-index:70;perspective:2000px;pointer-events:none;left:${start.left}px;top:${start.top}px;width:${start.width}px;height:${start.height}px`

  const inner = document.createElement("div")
  inner.style.cssText = "position:absolute;inset:0;transform-style:preserve-3d"

  const front = document.createElement("div")
  front.style.cssText = "position:absolute;inset:0;backface-visibility:hidden;overflow:hidden"
  const clone = cardEl.cloneNode(true) as HTMLElement
  clone.style.width = "100%"
  clone.style.height = "100%"
  clone.style.transform = ""
  clone.style.visibility = "visible"
  front.appendChild(clone)

  const back = document.createElement("div")
  back.style.cssText =
    "position:absolute;inset:0;backface-visibility:hidden;transform:rotateY(180deg);background:var(--sand-50);border-radius:18px"

  inner.append(front, back)
  morph.append(inner)
  document.body.appendChild(morph)
  return { morph, inner, back }
}

function run(
  morph: HTMLElement,
  inner: HTMLElement,
  back: HTMLElement,
  to: Box,
  flip: [number, number],
  radius: [string, string],
  done: () => void
) {
  const box = morph.animate([px(boxOf(morph)), px(to)], {
    duration: DURATION,
    easing: EASE,
    fill: "forwards",
  })
  inner.animate(
    [{ transform: `rotateY(${flip[0]}deg)` }, { transform: `rotateY(${flip[1]}deg)` }],
    {
      duration: DURATION,
      easing: EASE,
      fill: "forwards",
    }
  )
  back.animate([{ borderRadius: radius[0] }, { borderRadius: radius[1] }], {
    duration: DURATION,
    fill: "forwards",
  })
  box.onfinish = () => {
    done()
    morph.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160 }).onfinish = () =>
      morph.remove()
  }
}

function boxOf(el: HTMLElement): Box {
  return {
    left: parseFloat(el.style.left),
    top: parseFloat(el.style.top),
    width: parseFloat(el.style.width),
    height: parseFloat(el.style.height),
  }
}

/** Open: card grows from `cardEl` into `sheetEl`, flipping to the paper back. */
export function morphOpen(cardEl: HTMLElement, sheetEl: HTMLElement, onReveal: () => void) {
  const from: Box = ((r) => ({ left: r.left, top: r.top, width: r.width, height: r.height }))(
    cardEl.getBoundingClientRect()
  )
  const to = clipToView(sheetEl.getBoundingClientRect())
  sheetEl.style.visibility = "hidden"
  cardEl.style.visibility = "hidden"
  const { morph, inner, back } = makeMorph(cardEl, from)
  run(morph, inner, back, to, [0, 180], ["18px", "44px"], () => {
    sheetEl.style.visibility = "visible"
    onReveal()
  })
}

/** Close: the sheet shrinks back into `cardEl`, flipping the paper to the card. */
export function morphClose(cardEl: HTMLElement, sheetEl: HTMLElement, onDone: () => void) {
  const from = clipToView(sheetEl.getBoundingClientRect())
  const to: Box = ((r) => ({ left: r.left, top: r.top, width: r.width, height: r.height }))(
    cardEl.getBoundingClientRect()
  )
  sheetEl.style.visibility = "hidden"
  const { morph, inner, back } = makeMorph(cardEl, from)
  run(morph, inner, back, to, [180, 360], ["44px", "18px"], () => {
    cardEl.style.visibility = ""
    onDone()
  })
}
